import { useState, useMemo } from "react";
import { ArrowLeft, Calendar, Clock, MessageSquare, User, Stethoscope, CheckCircle, Info } from "lucide-react";
import PropTypes from "prop-types";
import "../css/schedulingPage.css";

const SchedulingPage = ({ patient, procedures, doctor, onSubmit, onBack }) => {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [time, setTime] = useState("");
  const [comments, setComments] = useState("Sem observações");
  const [errors, setErrors] = useState({});

  const today = new Date().toISOString().split("T")[0];

  /* ─────────── helpers ─────────── */
  const getSellingPrice = (proc) => {
    const byId = doctor?.procedures?.find((p) => p.id === proc.procedure_id)?.selling_price 
              ?? doctor?.procedures?.find((p) => p.id === proc.id)?.selling_price;

    if (typeof byId === "number" && !isNaN(byId)) return byId;
    return Number(proc.value ?? proc.cost_price ?? proc.price ?? 0);
  };

  const allInsurance = useMemo(() => procedures.every((p) => p.payment?.type === "insurance"), [procedures]);

  const totalPrice = useMemo(
    () => procedures.reduce((sum, proc) => {
      if (proc.payment?.type === "insurance") return sum;
      return sum + getSellingPrice(proc);
    }, 0),
    [procedures, doctor]
  );

  const formatCurrency = (v = 0) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!date) newErrors.date = "Selecione uma data";
    if (!time) newErrors.time = "Selecione um horário";

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    onSubmit({ date, time, comments, doctor, procedures, patient });
  };

  /* ─────────── JSX ─────────── */
  return (
    <div className="page-container scheduling-page">
      <div className="page-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </button>
        <h1>Agendar Procedimentos</h1>
      </div>

      <div className="scheduling-content">
        <div className="scheduling-info">
          <div className="info-card patient-info-card">
            <div className="info-card-header">
              <User size={20} />
              <h3>Paciente</h3>
            </div>
            <div className="info-card-content">
              <h4>{patient.name}</h4>
              <p>CPF: {patient.cpf}</p>
            </div>
          </div>

          <div className="info-card doctor-info-card">
            <div className="info-card-header">
              <Stethoscope size={20} />
              <h3>Profissional</h3>
            </div>
            <div className="info-card-content">
              <h4>{doctor.name}</h4>
              <p>{doctor.specialty}</p>
            </div>
          </div>

          <div className="info-card procedures-info-card">
            <div className="info-card-header">
              <CheckCircle size={20} />
              <h3>Procedimentos</h3>
            </div>
            <div className="info-card-content">
              <ul className="procedures-list-summary">
                {procedures.map((proc) => {
                  const sellingPrice = getSellingPrice(proc);
                  return (
                    <li key={proc.id}>
                      <span>{proc.name}</span>
                      <span className="procedure-price">
                        {proc.payment?.type === "insurance" ? "Convênio" : formatCurrency(sellingPrice)}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="total-price-container">
                <span>Valor&nbsp;Total:</span>
                <span className="total-price-value">
                  {allInsurance ? "Convênio" : formatCurrency(totalPrice)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <form className="scheduling-form" onSubmit={handleSubmit}>
          <h3 className="form-title">Selecione data e horário</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date"><Calendar size={18} /> <span>Data</span></label>
              <input
                type="date" id="date" value={date}
                onChange={(e) => setDate(e.target.value)}
                min={today}
                className={errors.date ? "error" : ""}
              />
              {errors.date && <p className="error-message">{errors.date}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="time"><Clock size={18} /> <span>Horário</span></label>
              <input
                type="time" id="time" value={time}
                onChange={(e) => setTime(e.target.value)}
                className={errors.time ? "error" : ""}
              />
              {errors.time && <p className="error-message">{errors.time}</p>}
            </div>
          </div>

          <div className="form-group comments-group">
            <label htmlFor="comments"><MessageSquare size={18} /> <span>Observações</span></label>
            <textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value || "Sem observações")}
              placeholder="Adicione informações relevantes para o agendamento (opcional)"
              rows={4}
            />
          </div>

          <div className="scheduling-note">
            <Info size={16} />
            <p>Ao confirmar, concorda com os termos. Cancelamentos com 24h de antecedência.</p>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onBack}>Cancelar</button>
            <button type="submit" className="submit-button">Revisar e Confirmar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

SchedulingPage.propTypes = {
  patient: PropTypes.object.isRequired,
  procedures: PropTypes.array.isRequired,
  doctor: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default SchedulingPage;